import { motion, AnimatePresence } from "framer-motion";
import { type FC, useEffect, useRef } from "react";

import { useOnboarding } from "../../hooks/useOnboarding";
import { LoadingState } from "../common/LoadingState";
import { OrgSwitcher } from "../dashboard/OrgSwitcher";
import { OnboardingStepper } from "./OnboardingStepper";
import { CreateOrgStep } from "./steps/CreateOrgStep";
import { DoneStep } from "./steps/DoneStep";
import { GitHubStep } from "./steps/GitHubStep";
import { JiraStep } from "./steps/JiraStep";
import { PaymentStep } from "./steps/PaymentStep";
import { PendingInvitesStep } from "./steps/PendingInvitesStep";

export const OnboardingWizard: FC = () => {
  const onboarding = useOnboarding();
  const stepContainerRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  // Focus management: move focus to step container on step change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (stepContainerRef.current && !onboarding.loading) {
      stepContainerRef.current.focus();
    }
  }, [onboarding.currentStep, onboarding.loading]);

  if (onboarding.loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingState variant="robot" message="Loading your workspace..." />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-arti-background">
      <div className="terminal-grid-bg absolute inset-0" />
      <div className="relative mx-auto flex min-h-screen max-w-lg flex-col items-center px-4 py-12">
        {/* Logo + title */}
        <img src="/arti.svg" alt="Arti" className="mb-4 h-10 w-10" />
        <h1 className="mb-2 font-mono text-sm font-bold uppercase tracking-widest text-white">
          Set up your workspace
        </h1>
        <p className="mb-8 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
          Configure integrations and billing
        </p>

        {/* Org switcher — shown when user has multiple orgs and is past org creation */}
        {onboarding.organizations.length > 1 &&
          onboarding.currentStep !== "pending-invites" &&
          onboarding.currentStep !== "create-org" && (
            <div className="mb-6 w-full border-2 border-slate-700 bg-arti-panel px-4 py-3">
              <p className="mb-1 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Configuring
              </p>
              <OrgSwitcher />
            </div>
          )}

        {/* Stepper */}
        <div className="mb-8 w-full">
          <OnboardingStepper
            steps={onboarding.steps}
            currentStep={onboarding.currentStep}
            completedSteps={onboarding.completedSteps}
          />
        </div>

        {/* Active step */}
        <AnimatePresence mode="wait">
          <motion.div
            key={onboarding.currentStep}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="w-full border-2 border-slate-700 bg-arti-panel p-6"
            ref={stepContainerRef}
            tabIndex={-1}
            style={{ outline: "none" }}
          >
            {onboarding.currentStep === "pending-invites" && (
              <PendingInvitesStep
                invites={onboarding.pendingInvites}
                onAccept={onboarding.acceptInviteAndProceed}
                onSkip={onboarding.skipStep}
                submitting={onboarding.submitting}
                error={onboarding.error}
              />
            )}

            {onboarding.currentStep === "create-org" && (
              <CreateOrgStep
                organizations={onboarding.organizations}
                onSubmit={onboarding.createOrg}
                onSelectExisting={onboarding.selectExistingOrg}
                submitting={onboarding.submitting}
                error={onboarding.error}
              />
            )}

            {onboarding.currentStep === "github" && (
              <GitHubStep
                isComplete={onboarding.completedSteps.has("github")}
                githubProviderId={onboarding.githubProviderId}
                onInstall={onboarding.installGitHub}
                onComplete={() => onboarding.markComplete("github")}
                onSkip={onboarding.skipStep}
                submitting={onboarding.submitting}
                error={onboarding.error}
              />
            )}

            {onboarding.currentStep === "jira" && (
              <JiraStep
                isComplete={onboarding.completedSteps.has("jira")}
                onComplete={() => onboarding.markComplete("jira")}
                onSkip={onboarding.skipStep}
              />
            )}

            {onboarding.currentStep === "payment" && (
              <PaymentStep
                isComplete={onboarding.completedSteps.has("payment")}
                onSetupPayment={onboarding.setupPayment}
                onSkip={onboarding.skipStep}
                submitting={onboarding.submitting}
                error={onboarding.error}
              />
            )}

            {onboarding.currentStep === "done" && (
              <DoneStep
                completedSteps={onboarding.completedSteps}
                onFinish={onboarding.finishOnboarding}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
